import React, { useState, useContext, useEffect, useRef } from 'react';
import { FaCamera, FaUser, FaLock, FaEnvelope, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { UserContext, SetUserContext } from '../../App';
import './Perfil.css';

const BASE = (process.env.REACT_APP_APIURL || 'http://localhost:4000').replace(/\/$/, '');
const api  = (p) => `${BASE}/${p}`;

const AVATAR_COLORS = ['#c9960c','#a87c0a','#10b981','#d4a843','#ef4444','#f0c040'];
const colorFor = (n) => AVATAR_COLORS[(n || 'U').charCodeAt(0) % AVATAR_COLORS.length];

const Toast = ({ msg, type }) => (
  <div className={`perfil-toast perfil-toast--${type}`}>{msg}</div>
);

export default function Perfil() {
  const user    = useContext(UserContext);
  const setUser = useContext(SetUserContext);

  // ── Personal info ──────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [userTag,   setUserTag]   = useState('');
  const [savingInfo, setSavingInfo] = useState(false);

  // ── Avatar ────────────────────────────────────────────────
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const fileInputRef = useRef(null);

  // ── Password ──────────────────────────────────────────────
  const [newPass,     setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [savingPass,  setSavingPass]  = useState(false);

  // ── Toast ─────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load profile ──────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName   || '');
    const raw = user.avatarUrl || '';
    setAvatarSrc(raw ? (raw.startsWith('/avatars') ? `${BASE}${raw}` : raw) : null);

    fetch(api(`usuario/${user.id}/profile`))
      .then(r => r.json())
      .then(p => {
        setFirstName(p.first_name || user.firstName || '');
        setLastName(p.last_name   || user.lastName  || '');
        setUserTag(p.user_tag || '');
        if (p.avatar_url) {
          const src = p.avatar_url.startsWith('/avatars') ? `${BASE}${p.avatar_url}` : p.avatar_url;
          setAvatarSrc(src);
          setUser(prev => ({ ...prev, avatarUrl: p.avatar_url }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const initials = firstName
    ? (firstName[0] + (lastName?.[0] || '')).toUpperCase()
    : (user?.username || 'U').slice(0, 2).toUpperCase();

  // ── Avatar upload ─────────────────────────────────────────
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return showToast('Imagen demasiado grande (máx 4 MB)', 'error');

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setUploadingAvatar(true);
      try {
        const res  = await fetch(api(`usuario/${user.id}/avatar`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64, mimeType: file.type }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        const src = `${BASE}${data.url}?t=${Date.now()}`;
        setAvatarSrc(src);
        setUser(prev => ({ ...prev, avatarUrl: data.url }));
        showToast('Foto actualizada');
      } catch (err) {
        showToast(err.message || 'Error subiendo imagen', 'error');
      } finally {
        setUploadingAvatar(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // ── Save info ─────────────────────────────────────────────
  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      const body = { first_name: firstName, last_name: lastName };
      if (user.firstRole) body.user_tag = userTag;
      const res = await fetch(api(`usuario/${user.id}/profile`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setUser(prev => ({ ...prev, firstName, lastName, userTag }));
      showToast('Datos guardados correctamente');
    } catch (err) {
      showToast(err.message || 'Error guardando datos', 'error');
    } finally {
      setSavingInfo(false);
    }
  };

  // ── Save password ─────────────────────────────────────────
  const savePassword = async () => {
    if (!newPass) return showToast('Escribe la nueva contraseña', 'error');
    if (newPass.length < 6) return showToast('Mínimo 6 caracteres', 'error');
    if (newPass !== confirmPass) return showToast('Las contraseñas no coinciden', 'error');
    setSavingPass(true);
    try {
      const res = await fetch(api(`usuario/${user.id}/password`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: newPass }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setNewPass('');
      setConfirmPass('');
      showToast('Contraseña actualizada. Vuelve a iniciar sesión para aplicar el cambio.');
    } catch (err) {
      showToast(err.message || 'Error cambiando contraseña', 'error');
    } finally {
      setSavingPass(false);
    }
  };

  if (!user) return null;

  return (
    <div className="perfil-page">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* ── Banner ── */}
      <div className="perfil-banner">
        <div className="perfil-banner-content">
          <div className="perfil-avatar-wrap">
            <div
              className="perfil-avatar"
              style={{ background: avatarSrc ? 'transparent' : colorFor(user.username), cursor: avatarSrc ? 'pointer' : 'default' }}
              onClick={() => avatarSrc && setShowAvatarModal(true)}
              title={avatarSrc ? 'Ver foto' : undefined}
            >
              {avatarSrc
                ? <img src={avatarSrc} alt="avatar" className="perfil-avatar-img" />
                : <span>{initials}</span>
              }
              {uploadingAvatar && <div className="perfil-avatar-loading"><div className="perfil-spinner" /></div>}
            </div>
            <button
              className="perfil-avatar-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Cambiar foto"
              disabled={uploadingAvatar}
            >
              <FaCamera />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
          <div className="perfil-banner-info">
            <h1 className="perfil-name">
              {firstName ? `${firstName} ${lastName}`.trim() : user.username}
            </h1>
            <p className="perfil-username">@{user.username}</p>
            {user.firstRole && <span className="perfil-badge">{userTag || 'Administrador'}</span>}
          </div>
        </div>
      </div>

      <div className="perfil-body">
        {/* ── Información personal ── */}
        <div className="perfil-card">
          <div className="perfil-card-header">
            <FaUser className="perfil-card-icon" />
            <div>
              <h2 className="perfil-card-title">Información personal</h2>
              <p className="perfil-card-sub">Tu nombre visible en la plataforma</p>
            </div>
          </div>
          <div className="perfil-fields">
            <div className="perfil-field">
              <label>Nombre</label>
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Tu nombre"
              />
            </div>
            <div className="perfil-field">
              <label>Apellidos</label>
              <input
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Tus apellidos"
              />
            </div>
            {user.firstRole && (
              <div className="perfil-field perfil-field--full">
                <label>Rol / Badge en el chat</label>
                <input
                  value={userTag}
                  onChange={e => setUserTag(e.target.value)}
                  placeholder="Ej: Instructor, Coach, Admin…"
                  maxLength={30}
                />
                <p className="perfil-hint">Visible junto a tu nombre en cada mensaje del chat.</p>
              </div>
            )}
          </div>
          <div className="perfil-actions">
            <button className="perfil-btn perfil-btn--primary" onClick={saveInfo} disabled={savingInfo}>
              {savingInfo ? 'Guardando…' : <><FaCheck /> Guardar cambios</>}
            </button>
          </div>
        </div>

        {/* ── Cuenta ── */}
        <div className="perfil-card">
          <div className="perfil-card-header">
            <FaEnvelope className="perfil-card-icon" />
            <div>
              <h2 className="perfil-card-title">Cuenta</h2>
              <p className="perfil-card-sub">Datos de acceso (no editables)</p>
            </div>
          </div>
          <div className="perfil-fields">
            <div className="perfil-field">
              <label>Nombre de usuario</label>
              <input value={user.username} disabled className="perfil-input--readonly" />
            </div>
            <div className="perfil-field">
              <label>Correo electrónico</label>
              <input
                value={user.email || 'No configurado'}
                disabled
                className="perfil-input--readonly"
              />
            </div>
          </div>
          <p className="perfil-hint">El correo electrónico y el nombre de usuario no pueden modificarse desde aquí.</p>
        </div>

        {/* ── Seguridad ── */}
        <div className="perfil-card">
          <div className="perfil-card-header">
            <FaLock className="perfil-card-icon" />
            <div>
              <h2 className="perfil-card-title">Seguridad</h2>
              <p className="perfil-card-sub">Cambia tu contraseña de acceso</p>
            </div>
          </div>
          <div className="perfil-fields">
            <div className="perfil-field perfil-field--full">
              <label>Nueva contraseña</label>
              <div className="perfil-pass-wrap">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
                <button
                  type="button"
                  className="perfil-pass-toggle"
                  onClick={() => setShowPass(p => !p)}
                >
                  {showPass ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="perfil-field perfil-field--full">
              <label>Confirmar contraseña</label>
              <div className="perfil-pass-wrap">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={confirmPass}
                  onChange={e => setConfirmPass(e.target.value)}
                  placeholder="Repite la contraseña"
                  className={confirmPass && confirmPass !== newPass ? 'perfil-input--error' : ''}
                />
              </div>
              {confirmPass && confirmPass !== newPass && (
                <span className="perfil-field-error">Las contraseñas no coinciden</span>
              )}
            </div>
          </div>
          <div className="perfil-actions">
            <button
              className="perfil-btn perfil-btn--primary"
              onClick={savePassword}
              disabled={savingPass || !newPass || newPass !== confirmPass}
            >
              {savingPass ? 'Cambiando…' : <><FaLock /> Cambiar contraseña</>}
            </button>
          </div>
        </div>
      </div>

      {showAvatarModal && avatarSrc && (
        <div className="perfil-modal-overlay" onClick={() => setShowAvatarModal(false)}>
          <img src={avatarSrc} alt="avatar" className="perfil-modal-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
